import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Subscription } from 'rxjs';
import { SessionService } from '../../../core/services/session.service';
import { Store } from '@ngrx/store';
import { AuthActions } from '../../../core/store/auth/auth.actions';

/**
 * SessionWarningComponent
 *
 * Dialog shown 2 minutes before the session expires.
 * Lets the user extend the session or log out immediately.
 */
@Component({
  selector: 'app-session-warning',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressBarModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="warning-header">
      <mat-icon color="warn">timer</mat-icon>
      <h2 mat-dialog-title>Sesion a punto de expirar</h2>
    </div>

    <mat-dialog-content>
      <p>Your session will expire in:</p>
      <div class="countdown" [class.critical]="secondsLeft() <= 30">
        {{ secondsLeft() | number: '1.0-0' }}s
      </div>
      <mat-progress-bar
        mode="determinate"
        [value]="progressValue()"
        color="warn">
      </mat-progress-bar>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button color="warn" (click)="logoutNow()">
        <mat-icon>logout</mat-icon>
        Close sesion
      </button>
      <button mat-raised-button color="primary" (click)="extendSession()">
        <mat-icon>refresh</mat-icon>
        Extender sesion
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    :host { display: block; min-width: 320px; }
    .warning-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 16px 24px 0;
      color: var(--color-figma-semantic-status-warning-foreground);
    }
    .warning-header h2 {
      margin: 0;
      font-size: var(--typography-figma-semantic-heading-h2-font-size);
      font-weight: var(--typography-figma-semantic-heading-h2-font-weight);
      line-height: var(--typography-figma-semantic-heading-h2-line-height);
    }
    .countdown {
      font-size: var(--typography-figma-semantic-display-font-size);
      font-weight: var(--typography-figma-semantic-display-font-weight);
      line-height: var(--typography-figma-semantic-display-line-height);
      text-align: center;
      margin: 16px 0 8px;
      transition: color 0.3s;
    }
    .countdown.critical { color: var(--color-figma-semantic-status-danger-foreground); }
  `],
})
export class SessionWarningComponent implements OnInit, OnDestroy {
  private readonly sessionService = inject(SessionService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly dialogRef = inject(MatDialogRef<SessionWarningComponent>);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly secondsLeft = signal<number>(120);
  readonly progressValue = () => (this.secondsLeft() / 120) * 100;

  private sub?: Subscription;

  ngOnInit(): void {
    this.sub = this.sessionService.sessionWarning$.subscribe((secs) => {
      this.secondsLeft.set(secs);
      this.cdr.markForCheck();
    });
  }

  extendSession(): void {
    this.sessionService.resetTimer();
    this.dialogRef.close();
  }

  logoutNow(): void {
    this.store.dispatch(AuthActions.logoutRequested());
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
